import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, Flame, ShoppingBag } from 'lucide-react';
import { useScrollTo } from '../hooks/use-scroll-to';
import { CONTACT } from '../data/site';

const VIDEO_SOURCES = [
  { src: '/hero-video.mp4', type: 'video/mp4' },
  { src: '/hero-video.webm', type: 'video/webm' },
  {
    src: 'https://videos.pexels.com/video-files/4131833/4131833-hd_1920_1080_24fps.mp4',
    type: 'video/mp4',
  },
];
const POSTER = '/fotos/01.jpg';

const LINES = ['HAMBÚRGUER', 'DE VERDADE.', 'INGREDIENTES DE 1ª LINHA.'];

export default function Hero() {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen items-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          data-testid="hero-video-player"
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={POSTER}
        >
          {VIDEO_SOURCES.map((video) => (
            <source key={video.src} src={video.src} type={video.type} />
          ))}
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0F] via-[#0D0D0F]/50 to-black/60" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FF5500]/40 bg-[#0D0D0F]/60 px-4 py-2 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <Flame size={14} className="text-[#FF5500]" />
          <span className="font-mono-alt text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FFB800]">
            Hamburgueria artesanal · Nova Friburgo, RJ
          </span>
        </motion.div>

        <h1
          data-testid="hero-title-text"
          className="font-display text-[17vw] uppercase leading-[0.9] text-white sm:text-8xl lg:text-[7.5rem]"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    INGREDIENTES DE <span className="text-[#FF5500]">1ª LINHA.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="font-serif-accent mt-6 text-2xl text-[#FFB800]"
          data-testid="hero-slogan"
        >
          Artesanal de verdade!
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="mt-6 max-w-xl text-base text-zinc-300 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Blend de 120g feito na casa, pão australiano e molhos autorais.
          Sem atalhos. Só amor e obsessão.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo('cardapio')}
            data-testid="hero-menu-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#FF5500] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#0D0D0F] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_36px_rgba(255,85,0,0.5)]"
          >
            Ver cardápio
            <ArrowDown size={16} className="transition-transform group-hover:translate-y-0.5" />
          </button>
          <a
            href={CONTACT.ifood}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-ifood-cta-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-[#EA1D2C] hover:bg-[#EA1D2C] hover:shadow-[0_0_36px_rgba(234,29,44,0.45)]"
          >
            <ShoppingBag size={16} /> Pedir online
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 sm:flex"
      >
        <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
          Role para sentir o amor
        </span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={14} className="text-[#FF5500]" />
        </motion.span>
      </motion.div>
    </section>
  );
}
